import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { PlusCircle, RotateCcw, Eye, Mail, CheckCircle, AlertTriangle, Clock, XCircle, FileText, DollarSign } from 'lucide-react';
import {
  useLoans,
  useLoanStatistics,
  useOverdueLoans,
  useActiveLoans,
  useCreateLoan,
  useReturnBook,
  useRenewLoan,
  usePendingBorrowRequests,
  useApproveBorrowRequest,
  useRejectBorrowRequest,
  useOutstandingFines,
  useLoanPolicies,
  useLoanHistory,
  Loan,
  Fine,
  LoanPolicy,
  LoanHistoryRecord,
} from '@/hooks';
import { useBooks, useUsers, useBookCopies } from '@/hooks';
import { useDensity } from '@/components/admin/context/DensityContext';
import LibButton from '@/components/librarian/ui/LibButton';

interface CreateLoanFormState {
  bookId: string;
  userId: string;
  dueDate: string;
  notes: string;
}

const TABS = ['All', 'Active', 'Overdue', 'Returned'] as const;
type TabValue = typeof TABS[number];

const LoansModule: React.FC = () => {
  const { toast } = useToast();
  const { density } = useDensity();
  const { data: loansPage, isLoading: loansLoading } = useLoans();
  const apiLoans: Loan[] = (loansPage as any)?.results || [];
  const { data: stats } = useLoanStatistics();
  const { data: overdueData } = useOverdueLoans();
  const { data: activeData } = useActiveLoans();
  const { data: finesData } = useOutstandingFines();
  const { data: policiesData } = useLoanPolicies();
  const { data: historyData } = useLoanHistory();
  const { data: booksData } = useBooks();
  const { data: usersData } = useUsers();
  const { data: bookCopiesData } = useBookCopies({ status: 'available' });
  const { data: pendingRequestsData, isLoading: pendingRequestsLoading } = usePendingBorrowRequests();
  const approveRequestMutation = useApproveBorrowRequest();
  const rejectRequestMutation = useRejectBorrowRequest();

  const createLoanMutation = useCreateLoan();
  const [selectedLoanIdForReturn, setSelectedLoanIdForReturn] = useState<number | null>(null);
  const [selectedLoanIdForRenew, setSelectedLoanIdForRenew] = useState<number | null>(null);

  const returnBookMutation = useReturnBook(selectedLoanIdForReturn || 0);
  const renewLoanMutation = useRenewLoan(selectedLoanIdForRenew || 0);

  const [activeTab, setActiveTab] = useState<TabValue>('All');
  const [isNewLoanDialogOpen, setIsNewLoanDialogOpen] = useState(false);
  const [viewingLoan, setViewingLoan] = useState<Loan | null>(null);
  const [selectedRequestForReject, setSelectedRequestForReject] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const [loanForm, setLoanForm] = useState<CreateLoanFormState>({
    bookId: '',
    userId: '',
    dueDate: '',
    notes: '',
  });

  const [bookSearchQuery, setBookSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const loans: Loan[] = useMemo(() => apiLoans, [apiLoans]);
  const pendingRequests: any[] = (pendingRequestsData as any)?.requests || [];

  // Tab-filtered loans
  const displayedLoans = useMemo(() => {
    if (activeTab === 'All') return loans;
    const statusMap: Record<TabValue, string> = {
      All: '',
      Active: 'active',
      Overdue: 'overdue',
      Returned: 'returned',
    };
    const targetStatus = statusMap[activeTab];
    return loans.filter(l => l.status === targetStatus);
  }, [loans, activeTab]);

  const handleCreateLoan = () => {
    if (!loanForm.bookId || !loanForm.userId || !loanForm.dueDate) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    createLoanMutation.mutate({
      book_copy: Number(loanForm.bookId),
      borrower: Number(loanForm.userId),
      due_date: new Date(loanForm.dueDate).toISOString(),
      notes: loanForm.notes || '',
    } as any, {
      onSuccess: () => {
        setIsNewLoanDialogOpen(false);
        setLoanForm({ bookId: '', userId: '', dueDate: '', notes: '' });
        toast({ title: 'Success', description: 'Loan created successfully' });
      },
      onError: (e: any) => {
        toast({ title: 'Create failed', description: String(e), variant: 'destructive' });
      },
    });
  };

  const handleReturnBook = (loanId: number | string) => {
    const numericId = typeof loanId === 'number' ? loanId : Number(String(loanId).replace(/\D/g, ''));
    setSelectedLoanIdForReturn(numericId);
    returnBookMutation.mutate({}, {
      onSuccess: () => {
        toast({ title: 'Book Returned', description: 'Book has been successfully returned' });
        setSelectedLoanIdForReturn(null);
      },
      onError: (e: any) => {
        toast({ title: 'Return failed', description: String(e), variant: 'destructive' });
        setSelectedLoanIdForReturn(null);
      },
    });
  };

  const handleRenewLoan = (loanId: number | string) => {
    const numericId = typeof loanId === 'number' ? loanId : Number(String(loanId).replace(/\D/g, ''));
    setSelectedLoanIdForRenew(numericId);
    renewLoanMutation.mutate({}, {
      onSuccess: () => {
        toast({ title: 'Loan Renewed', description: 'Loan period has been extended' });
        setSelectedLoanIdForRenew(null);
      },
      onError: (e: any) => {
        toast({ title: 'Renew failed', description: String(e), variant: 'destructive' });
        setSelectedLoanIdForRenew(null);
      },
    });
  };

  const handleSendReminder = (_loanId: number | string) => {
    toast({ title: 'Reminder Sent', description: 'Email reminder has been sent to the user' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'overdue': return 'destructive';
      case 'returned': return 'secondary';
      case 'lost': return 'destructive';
      case 'damaged': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Clock className="h-4 w-4" />;
      case 'overdue': return <AlertTriangle className="h-4 w-4" />;
      case 'returned': return <CheckCircle className="h-4 w-4" />;
      case 'lost': return <XCircle className="h-4 w-4" />;
      case 'damaged': return <XCircle className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const activeLoansCount = (activeData as any)?.count ?? loans.filter(l => l.status === 'active').length;
  const overdueLoansCount = (overdueData as any)?.count ?? loans.filter(l => l.status === 'overdue').length;
  const totalLoansCount = (loansPage as any)?.count ?? loans.length;

  return (
    <div className="space-y-5 max-w-[1400px]">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)]">Loans</h1>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Track borrowing and returns.</p>
        </div>
        <LibButton size="none"
          onClick={() => setIsNewLoanDialogOpen(true)} className="flex gap-1.5 px-3 py-2 text-sm font-medium hover:bg-[var(--lib-gold)] rounded-lg transition-colors"
        >
          <PlusCircle className="h-4 w-4" />
          New Loan
        </LibButton>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Loans', value: totalLoansCount.toLocaleString() },
          { label: 'Active', value: activeLoansCount.toLocaleString() },
          { label: 'Overdue', value: overdueLoansCount.toLocaleString() },
          { label: 'Returned Today', value: '0' },
        ].map(s => (
          <div key={s.label} className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg px-5 py-4">
            <div className="font-display text-[22px] font-bold text-[var(--lib-ink)]">{s.value}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Pill tabs */}
      <div className="flex gap-1 bg-[var(--lib-surface-2)] p-1 rounded-lg w-fit">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab ? 'bg-[var(--lib-surface)] text-[var(--lib-ink)] shadow-sm' : 'text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)]'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[var(--lib-surface-2)] border-b border-[var(--lib-hairline)]">
            <tr>
              <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Book</th>
              <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Borrower</th>
              <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Issue Date</th>
              <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Due Date</th>
              <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Status</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--lib-hairline)]">
            {displayedLoans.map(loan => {
              const status = loan.status;
              const statusConfig = {
                active: { bg: 'bg-[var(--lib-success)]/10', text: 'text-[var(--lib-success)]', border: 'border-[var(--lib-success)]/30', label: 'Active' },
                overdue: { bg: 'bg-[var(--lib-danger)]/10', text: 'text-[var(--lib-danger)]', border: 'border-[var(--lib-danger)]/30', label: 'Overdue' },
                returned: { bg: 'bg-[var(--lib-surface-2)]', text: 'text-[var(--lib-ink-muted)]', border: 'border-[var(--lib-hairline)]', label: 'Returned' },
                pending: { bg: 'bg-[var(--lib-gold)]/10', text: 'text-[var(--lib-gold-text)]', border: 'border-[var(--lib-gold)]/30', label: 'Pending' },
                lost: { bg: 'bg-[var(--lib-danger)]/10', text: 'text-[var(--lib-danger)]', border: 'border-[var(--lib-danger)]/30', label: 'Lost' },
                damaged: { bg: 'bg-[var(--lib-gold)]/10', text: 'text-[var(--lib-gold-text)]', border: 'border-[var(--lib-gold)]/30', label: 'Damaged' },
              }[status] ?? { bg: 'bg-[var(--lib-surface-2)]', text: 'text-[var(--lib-ink-muted)]', border: 'border-[var(--lib-hairline)]', label: status };

              return (
                <tr key={loan.id} className="hover:bg-[var(--lib-surface-2)] transition-colors group">
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm font-medium text-[var(--lib-ink)]`}>
                    {loan.book_copy_info?.book_title ?? '—'}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm text-[var(--lib-ink-muted)]`}>
                    {loan.borrower_info?.full_name ?? '—'}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm text-[var(--lib-ink-muted)] font-mono`}>
                    {loan.loan_date ? new Date(loan.loan_date).toLocaleDateString() : '—'}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm text-[var(--lib-ink-muted)] font-mono`}>
                    {loan.due_date ? new Date(loan.due_date).toLocaleDateString() : '—'}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'}`}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}>
                      {statusConfig.label}
                    </span>
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'}`}>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                      <button
                        onClick={() => setViewingLoan(loan)}
                        className="p-1 text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] transition-colors"
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {(status === 'active' || status === 'overdue') && (
                        <>
                          <button
                            onClick={() => handleReturnBook(loan.id)}
                            className="px-2.5 py-1 text-xs font-medium border border-[var(--lib-gold)] text-[var(--lib-navy)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
                          >
                            Return
                          </button>
                          {loan.can_renew_status?.can_renew && (
                            <button
                              onClick={() => handleRenewLoan(loan.id)}
                              className="px-2.5 py-1 text-xs font-medium border border-[var(--lib-navy)]/30 text-[var(--lib-navy)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
                            >
                              Renew
                            </button>
                          )}
                          {status === 'overdue' && (
                            <button
                              onClick={() => handleSendReminder(loan.id)}
                              className="px-2.5 py-1 text-xs font-medium text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] transition-colors"
                            >
                              Remind
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {displayedLoans.length === 0 && (
          <div className="py-12 text-center text-sm text-[var(--lib-ink-muted)]">
            {loansLoading ? 'Loading loans…' : 'No loans found'}
          </div>
        )}
      </div>

      {/* Pending Borrow Requests */}
      {pendingRequests.length > 0 && (
        <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-[var(--lib-hairline)] bg-[var(--lib-surface-2)]">
            <h2 className="text-sm font-semibold text-[var(--lib-ink)]">Pending Borrow Requests</h2>
            <p className="text-xs text-[var(--lib-ink-muted)] mt-0.5">{pendingRequests.length} request(s) awaiting approval</p>
          </div>
          <table className="w-full">
            <thead className="border-b border-[var(--lib-hairline)]">
              <tr>
                <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Request</th>
                <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Book</th>
                <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">User</th>
                <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Requested</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--lib-hairline)]">
              {pendingRequests.map((req: any) => (
                <tr key={req.id} className="hover:bg-[var(--lib-surface-2)] transition-colors group">
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm font-mono text-[var(--lib-ink-muted)]`}>#{req.id}</td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm font-medium text-[var(--lib-ink)]`}>{req.book_title || req.book}</td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm text-[var(--lib-ink-muted)]`}>{req.borrower_name || req.borrower}</td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm text-[var(--lib-ink-muted)] font-mono`}>{new Date(req.created_at).toLocaleDateString()}</td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'}`}>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                      <button
                        onClick={() => approveRequestMutation.mutate(req.id, {
                          onSuccess: () => toast({ title: 'Request approved', description: `Request #${req.id} approved and loan created` }),
                          onError: (e: any) => toast({ title: 'Approve failed', description: String(e), variant: 'destructive' }),
                        })}
                        className="px-2.5 py-1 text-xs font-medium bg-[var(--lib-success)]/10 border border-[var(--lib-success)]/30 text-[var(--lib-success)] rounded-lg hover:bg-[var(--lib-success)]/20 transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => setSelectedRequestForReject(req.id)}
                        className="px-2.5 py-1 text-xs font-medium border border-[var(--lib-danger)]/30 text-[var(--lib-danger)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Loan Dialog */}
      <Dialog open={isNewLoanDialogOpen} onOpenChange={setIsNewLoanDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Loan</DialogTitle>
            <DialogDescription>Select a book and user to create a new loan.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="book">Book</Label>
              <Select value={loanForm.bookId} onValueChange={(value) => setLoanForm({ ...loanForm, bookId: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a book" />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-3 py-2">
                    <Input
                      placeholder="Search books by title or barcode..."
                      value={bookSearchQuery}
                      onChange={(e) => setBookSearchQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  {(bookCopiesData?.results || [])
                    .filter(copy => copy.status === 'available')
                    .filter(copy => {
                      const q = bookSearchQuery.trim().toLowerCase();
                      if (!q) return true;
                      const title = (copy.book_info?.title || '').toLowerCase();
                      const barcode = (copy.barcode || '').toLowerCase();
                      return title.includes(q) || barcode.includes(q);
                    })
                    .map(copy => (
                      <SelectItem key={copy.id} value={copy.id.toString()}>
                        {copy.book_info?.title || 'Unknown Book'} ({copy.barcode})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user">User</Label>
              <Select value={loanForm.userId} onValueChange={(value) => setLoanForm({ ...loanForm, userId: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-3 py-2">
                    <Input
                      placeholder="Search users by name, email or username..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  {(usersData?.results || [])
                    .filter(user => user.is_active)
                    .filter(user => {
                      const q = userSearchQuery.trim().toLowerCase();
                      if (!q) return true;
                      const name = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
                      const email = (user.email || '').toLowerCase();
                      const username = (user.username || '').toLowerCase();
                      return name.includes(q) || email.includes(q) || username.includes(q);
                    })
                    .map(user => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.first_name} {user.last_name} ({user.user_type || user.role})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date</Label>
              <Input
                id="dueDate"
                type="date"
                value={loanForm.dueDate}
                onChange={(e) => setLoanForm({ ...loanForm, dueDate: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewLoanDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateLoan} disabled={!loanForm.bookId || !loanForm.userId || !loanForm.dueDate}>
              Create Loan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Request Dialog */}
      <Dialog open={!!selectedRequestForReject} onOpenChange={() => { setSelectedRequestForReject(null); setRejectReason(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Borrow Request</DialogTitle>
            <DialogDescription>Provide a reason for rejecting the borrow request.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedRequestForReject(null); setRejectReason(''); }}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!selectedRequestForReject) return;
                rejectRequestMutation.mutate({ requestId: selectedRequestForReject, reason: rejectReason }, {
                  onSuccess: () => {
                    toast({ title: 'Request rejected', description: `Request #${selectedRequestForReject} rejected` });
                    setSelectedRequestForReject(null);
                    setRejectReason('');
                  },
                  onError: (e: any) => toast({ title: 'Reject failed', description: String(e), variant: 'destructive' }),
                });
              }}
              disabled={!rejectReason.trim()}
            >
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Loan Details Dialog */}
      <Dialog open={!!viewingLoan} onOpenChange={() => setViewingLoan(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Loan Details</DialogTitle>
            <DialogDescription>Complete information for loan #{viewingLoan?.id}</DialogDescription>
          </DialogHeader>
          {viewingLoan && (
            <div className="space-y-4">
              <div className="grid gap-3">
                <div>
                  <label className="text-sm font-medium text-[var(--lib-ink-muted)]">Loan ID</label>
                  <p className="text-sm font-mono">{viewingLoan.id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--lib-ink-muted)]">Book</label>
                  <p className="text-sm font-medium">{viewingLoan.book_copy_info?.book_title || 'Unknown Book'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--lib-ink-muted)]">User</label>
                  <p className="text-sm">
                    {viewingLoan.borrower_info?.full_name || 'Unknown User'}
                    <br />
                    <span className="text-[var(--lib-ink-muted)]">{viewingLoan.borrower_info?.email || ''}</span>
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-[var(--lib-ink-muted)]">Checkout Date</label>
                    <p className="text-sm">{new Date(viewingLoan.loan_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[var(--lib-ink-muted)]">Due Date</label>
                    <p className="text-sm">{new Date(viewingLoan.due_date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--lib-ink-muted)]">Status</label>
                  <div className="mt-1">
                    <Badge variant={getStatusColor(viewingLoan.status) as any}>
                      <div className="flex items-center space-x-1">
                        {getStatusIcon(viewingLoan.status)}
                        <span>{viewingLoan.status}</span>
                      </div>
                    </Badge>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-[var(--lib-ink-muted)]">Renewals</label>
                    <p className="text-sm">{viewingLoan.renewed_count} of {viewingLoan.can_renew_status?.reason || 'N/A'}</p>
                  </div>
                  {Number(viewingLoan.current_fine) > 0 && (
                    <div>
                      <label className="text-sm font-medium text-[var(--lib-ink-muted)]">Fine Amount</label>
                      <p className="text-sm font-medium text-[var(--lib-danger)]">${Number(viewingLoan.current_fine).toFixed(2)}</p>
                    </div>
                  )}
                </div>
                {viewingLoan.notes && (
                  <div>
                    <label className="text-sm font-medium text-[var(--lib-ink-muted)]">Notes</label>
                    <p className="text-sm text-[var(--lib-ink-muted)] whitespace-pre-wrap">{viewingLoan.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingLoan(null)}>Close</Button>
            {viewingLoan && (viewingLoan.status === 'active' || viewingLoan.status === 'overdue') && (
              <div className="flex space-x-2">
                <Button variant="outline" onClick={() => { handleReturnBook(viewingLoan.id); setViewingLoan(null); }}>
                  Return Book
                </Button>
                {viewingLoan.can_renew_status?.can_renew && (
                  <Button onClick={() => { handleRenewLoan(viewingLoan.id); setViewingLoan(null); }}>
                    Renew Loan
                  </Button>
                )}
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoansModule;
